import { useState, useCallback } from "react";
import { message } from "antd";
import { authAxios } from "../../utils/session_utils";

const API_BASE = process.env.REACT_APP_BACK_END_API || "";

interface IngestResponse {
	success: boolean;
	s3Key?: string;
	message?: string;
	error?: string;
}

const useVideoUrlIngestHook = () => {
	const [loading, setLoading] = useState<boolean>(false);
	const [url, setUrl] = useState<string>("");

	const handleUrlChange = useCallback((value: string) => {
		setUrl(value);
	}, []);

	const handleIngest = useCallback(async () => {
		if (!url || !url.trim()) {
			message.error("Please enter a video URL");
			return;
		}

		// Basic URL validation
		try {
			const urlObj = new URL(url.trim());
			if (!urlObj.protocol.startsWith('http')) {
				message.error("URL must use http or https protocol");
				return;
			}
		} catch (error) {
			message.error("Please enter a valid URL");
			return;
		}

		setLoading(true);
		const hideLoading = message.loading("Validating URL...", 0);

		try {
			const config = {
				method: "POST",
				url: `${API_BASE}/video-compliance/ingest-from-url`,
				headers: {
					"Content-Type": "application/json",
				},
				data: {
					url: url.trim(),
				},
			};

			const response = await authAxios(config);
			const data = response.data as IngestResponse;

			if (data.success) {
				hideLoading();
				message.success("Ingestion is in progress. You will receive an email notification if there are any errors.");
				setUrl(""); // Clear URL on success
			} else {
				throw new Error(data.error || "Ingest failed");
			}
		} catch (error: any) {
			console.error("Error ingesting video:", error);
			const errorMessage =
				error.response?.data?.error ||
				error.response?.data?.message ||
				error.message ||
				"Failed to start video ingestion";
			message.error(`Ingest failed: ${errorMessage}`);
		} finally {
			setLoading(false);
			hideLoading();
		}
	}, [url]);

	return {
		loading,
		url,
		handleUrlChange,
		handleIngest,
	};
};

export default useVideoUrlIngestHook;
